import {
  useDeleteChemicalData,
  useDeleteChemicalTransportData,
  usePostChemicalData,
  usePostChemicalTransportData,
  usePutChemicalData,
  usePutChemicalTransportData
} from "../../../../../api/chemical";
import { errorView, successMessage } from "../../../../../helpers/ErrorHandler";
import { ChemicalRecord, TransportRoute } from "../../../../../types/process/process.types";
import { GateKind } from "../types/processDetail.types";
import { mapRecordToApiChemical, mapRouteToApiTransport } from "../utils/processDetail.utils";

interface UseProcessChemicalCrudParams {
  processId: string;
  gateKind: GateKind;
  onSaved: () => void;
}

/** Chemical + transport-route CRUD against the dedicated processing-chemical endpoints. */
export const useProcessChemicalCrud = ({ processId, gateKind, onSaved }: UseProcessChemicalCrudParams) => {
  const type = gateKind === "farm" ? "farm" : "process";
  const supplierDataHandle = false;

  const { mutate: postChemical } = usePostChemicalData();
  const { mutate: putChemical } = usePutChemicalData();
  const { mutate: deleteChemical } = useDeleteChemicalData();
  const { mutate: postTransport } = usePostChemicalTransportData();
  const { mutate: putTransport } = usePutChemicalTransportData();
  const { mutate: deleteTransport } = useDeleteChemicalTransportData();

  const addChemical = (initial: Partial<ChemicalRecord> = {}) => {
    postChemical(
      {
        supplierDataHandle,
        type,
        processingId: processId,
        chemicalData: mapRecordToApiChemical({
          name: initial.name ?? "",
          function: initial.function ?? "",
          amount: initial.amount ?? "",
          hasTransportRoute: initial.hasTransportRoute ?? false
        })
      },
      {
        onSuccess: () => {
          successMessage("Chemical added");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to add chemical")
      }
    );
  };

  const updateChemical = (id: string, patch: Partial<ChemicalRecord>, current: ChemicalRecord) => {
    const merged = { ...current, ...patch };
    putChemical(
      {
        supplierDataHandle,
        type,
        chemicalId: id,
        chemicalData: mapRecordToApiChemical(merged)
      },
      {
        onSuccess: () => {
          successMessage("Chemical updated");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to update chemical")
      }
    );
  };

  const removeChemical = (id: string) => {
    deleteChemical(
      { supplierDataHandle, type, chemicalData: { chemicalId: id, processingId: processId } },
      {
        onSuccess: () => {
          successMessage("Chemical removed");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to remove chemical")
      }
    );
  };

  const addRoute = (chemId: string, route: Pick<TransportRoute, "type" | "distance">) => {
    postTransport(
      {
        supplierDataHandle,
        type,
        chemicalId: chemId,
        transportData: mapRouteToApiTransport({ id: "", type: route.type, distance: route.distance })
      },
      {
        onSuccess: () => {
          successMessage("Transport route added");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to add transport route")
      }
    );
  };

  const updateRoute = (routeId: string, route: TransportRoute) => {
    putTransport(
      {
        supplierDataHandle,
        type,
        chemicalTransportId: routeId,
        transportData: mapRouteToApiTransport(route)
      },
      {
        onSuccess: () => {
          successMessage("Transport route updated");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to update transport route")
      }
    );
  };

  const removeRoute = (chemId: string, routeId: string) => {
    deleteTransport(
      { supplierDataHandle, type, chemicalTransportId: routeId, chemicalId: chemId },
      {
        onSuccess: () => {
          successMessage("Transport route removed");
          onSaved();
        },
        onError: (error: any) => errorView(error?.msg || "Failed to remove transport route")
      }
    );
  };

  return { addChemical, updateChemical, removeChemical, addRoute, updateRoute, removeRoute };
};
